import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import Header from './components/Header.jsx';
import Modal from './components/Modal.jsx';
import QueryContainer from './components/QueryContainer.jsx';
import ResultsContainer from './components/ResultsContainer.jsx';

import './App.css';
import './index.js';
import { MAX_QUERY_HISTORY_LENGTH } from './utils/constants.js';

function App() {
    const [queryHistory, setQueryHistory] = useState(() => {
        const saved = localStorage.getItem('queryHistory');
        return saved ? JSON.parse(saved) : [];
    });
    const [correlationsMap, setCorrelationsMap] = useState({});
    const [isSidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [modalSrc, setModalSrc] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const abortControllerRef = useRef(null);

    // Whenever queryHistory changes, save to localStorage
    useEffect(() => {
        localStorage.setItem('queryHistory', JSON.stringify(queryHistory));
    }, [queryHistory]);

    /** Toggle sidebar state */
    const handleToggleSidebar = () => {
        setSidebarCollapsed(!isSidebarCollapsed);
        // HACK: tell Plotly to resize graph (if there is one) after collapsing
        setTimeout(() => window.dispatchEvent(new Event('resize')), 200);
    };

    /** Scroll to top of screen */
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    /** Fetch correlation data from API when user clicks 'query' button */
    const handleQuery = (query) => {
        // Cancel any previous request
        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;
        setIsLoading(true);
        scrollToTop();

        // Add this query to history, retaining the given max number
        setQueryHistory((prev) => [
            query,
            ...prev.slice(0, MAX_QUERY_HISTORY_LENGTH - 1),
        ]);

        const endpoint = query.tcga
            ? `${process.env.REACT_APP_API_ROOT}correlations_tcga/`
            : `${process.env.REACT_APP_API_ROOT}correlations/`;

        axios.post(
            endpoint,
            {
                feature1: query.feature1,
                feature2: query.feature2,
                database1: query.database1,
                database2: query.database2,
                subcategory1: query.subcategory1,
                subcategory2: query.subcategory2,
                minCorrelation: query.minCorrelation,
                maxPValue: query.maxPValue,
            },
            {
                signal: controller.signal,
            }
        )
            .then((response) => {
                setCorrelationsMap(response.data.correlations);
            })
            .catch((err) => {
                console.error('Error fetching correlations:', err);
            })
            .finally(() => {
                setIsLoading(false);
                scrollToTop();
            });
    };

    /** Fetch correlation data when user clicks a feature in the table */
    const handleRequery = (newFeature1, newDatabase1) => {
        const last = queryHistory[0];
        if (!last) return;

        const updatedQuery = {
            ...last,
            feature1: newFeature1,
            database1: [newDatabase1],
            // HACK: helps to populate form fields, but may not work if >1 subcategories
            subcategory1: last.subcategory2,
        };
        handleQuery(updatedQuery);
    };

    // Modal openers
    const openNuclear = () =>
        setModalSrc('/Nuclear_Features.pdf#view=FitH&toolbar=0&navpanes=0&scrollbar=0');
    const openFracLac = () =>
        setModalSrc('/FracLac_Name_Codes.pdf#view=FitH&toolbar=0&navpanes=0&scrollbar=0');

    return (
        <div className="h-screen flex flex-col bg-gray-50">
            <Header />

            <main className="relative flex flex-1 overflow-y-auto custom-scrollbar">
                <QueryContainer
                    openNuclearModal={openNuclear}
                    openFracLacModal={openFracLac}
                    onQuery={handleQuery}
                    isCollapsed={isSidebarCollapsed}
                    queryHistory={queryHistory}
                    clearQueryHistory={() => setQueryHistory([])}
                    onToggleSidebar={handleToggleSidebar}
                />

                <ResultsContainer
                    correlationsMap={correlationsMap}
                    lastQuery={queryHistory[0] ?? {}}
                    onRequery={handleRequery}
                    isLoading={isLoading}
                    onCancel={() => abortControllerRef.current?.abort()}
                />
            </main>

            <Modal
                isOpen={!!modalSrc}
                onClose={() => setModalSrc(null)}
                src={modalSrc ?? ''}
            />
        </div>
    );
}

export default App;



